import React, { useMemo, useState } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import { useAuth } from 'contexts/AuthContext';
import { useGetManagerCenterData } from 'services/hooks/queries';
import { useUpdateAgentAvailabileStatusById } from 'services/hooks/mutations';
import { Button, Select, Table, TableColumn } from 'common/components';
import { UserRoundX } from 'lucide-react';
import { UserRole } from 'types/userRoles';
import { startOfDay, endOfDay } from 'date-fns';

import './ManagerCommandCenter.css';

type Agent = {
    name: string;
    status: string;
    calls_taken_today: number;
    id: string;
};

const ManagerCommandCenter = () => {
    const [statusFilter, setStatusFilter] = useState('All Statuses');

    const { userRoles } = useAuth();

    const isManager = userRoles.some(
        (role) => role === UserRole.MANAGER || role === UserRole.SUPER_ADMIN || role === UserRole.ADMIN
    );

    const startOfDate = startOfDay(new Date());
    const endOfDate = endOfDay(new Date());

    const { managerCenterData, isManagerCenterDataLoading } = useGetManagerCenterData({
        startDate: startOfDate,
        endDate: endOfDate,
    });

    const { updateAgentAvailabileStatusById } = useUpdateAgentAvailabileStatusById();

    // Handle status filter change
    const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatusFilter(e.target.value);
    };

    // Get unique statuses from agents data
    const uniqueStatuses = ['All Statuses', ...new Set(managerCenterData?.agents.map((agent) => agent.status))];

    // Render metric card with value and label
    const MetricCard = ({ label, value, color }: { label: string; value: number; color?: string }) => {
        const style = color ? { color } : {};
        return (
            <div className='metric-card'>
                <div className='metric-card-label'>{label}</div>
                <div className='metric-card-value' style={style}>
                    {value}
                </div>
            </div>
        );
    };

    // Helper function to render status badge with appropriate styling
    const renderStatusBadge = (status: string) => {
        let badgeClass = 'manager-command-center-status-badge';

        switch (status) {
            case 'Call in Progress':
                badgeClass += ' manager-command-center-status-badge-active';
                break;
            case 'Available':
                badgeClass += ' manager-command-center-status-badge-queue';
                break;
            case 'Offline':
                badgeClass += ' manager-command-center-status-badge-inactive';
                break;
            default:
                badgeClass += ' manager-command-center-status-badge-inactive';
        }

        return <span className={badgeClass}>{status}</span>;
    };

    // Status priority for sorting
    const getStatusPriority = (status: string) => {
        switch (status) {
            case 'Call in Progress':
                return 1;
            case 'Available':
                return 2;
            case 'Offline':
                return 3;
            default:
                return 4;
        }
    };

    // Filter and sort agents based on selected status and priorities
    const filteredAndSortedAgents = useMemo(() => {
        return managerCenterData
            ? (statusFilter === 'All Statuses'
                  ? [...managerCenterData.agents]
                  : managerCenterData.agents.filter((agent) => agent.status === statusFilter)
              )
                  // Then sort by status priority and name
                  .sort((a, b) => {
                      // First sort by status priority
                      const statusPriorityA = getStatusPriority(a.status);
                      const statusPriorityB = getStatusPriority(b.status);

                      if (statusPriorityA !== statusPriorityB) {
                          return statusPriorityA - statusPriorityB;
                      }

                      // If status is the same, sort alphabetically by name
                      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                  })
            : [];
    }, [managerCenterData?.agents, statusFilter]);

    const columns: TableColumn<Agent>[] = [
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => renderStatusBadge(record.status),
        },
        {
            key: 'calls_taken_today',
            title: 'Calls Taken Today',
            dataIndex: 'calls_taken_today',
        },
        {
            key: 'actions',
            title: '',
            align: 'right',
            render: (_, record) =>
                isManager && record.status === 'Available' ? (
                    <Button
                        variant='primary'
                        size='sm'
                        icon={UserRoundX}
                        onClick={() => updateAgentAvailabileStatusById({ id: record.id, status: false })}
                    >
                        Set Offline
                    </Button>
                ) : null,
        },
    ];

    if (isManagerCenterDataLoading || !managerCenterData) {
        return (
            <div className='loading'>
                <LoadingSpinner size={80} borderWidth={8} color='#9b51e0' className='' />
            </div>
        );
    }

    return (
        <div className='manager-command-center'>
            <h1>Manager Command Center</h1>

            {/* Metrics Grid */}
            <div className='metrics-grid'>
                <MetricCard label='Calls Taken' value={managerCenterData.metrics.calls_taken} />
                <MetricCard label='Online Agents' value={managerCenterData.metrics.online_agents} color='#25D366' />
            </div>

            {/* Agent Monitoring */}
            <div className='agent-monitoring-section'>
                <h2 className='section-title'>Agent Monitoring</h2>

                <div className='filters-bar'>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        options={uniqueStatuses.map((status) => ({
                            value: status,
                            label: status,
                        }))}
                    />
                </div>

                <div className='agent-table-container'>
                    <Table
                        data={filteredAndSortedAgents}
                        columns={columns}
                        rowKey='id'
                        hoverable
                        variant='secondary'
                        emptyText='No agents found'
                        className='agent-table'
                    />
                </div>
            </div>
        </div>
    );
};

export default ManagerCommandCenter;
